/**
 * Authentication Context Provider
 *
 * Provides auth state, login/logout actions, and permission checking
 * via React Context. No Redux dependency.
 *
 * Generated by portal_generator.py
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { LoginCredentials, AuthState } from './types';
import {
  loginAction,
  logoutAction,
  refreshAction,
  restoreAuth,
  tokenStorage,
} from './authStore';
import { emitSessionExpired, refreshOnce, resetSessionExpired } from './authClient';

// ---------------------------------------------------------------------------
// Context Types
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    // Restore session from localStorage on mount
    const user = restoreAuth();
    return {
      user,
      isAuthenticated: !!user,
      loading: false,
      error: null,
    };
  });

  // Proactive token refresh: check every 60s if token expires within 5 min
  // Belt: runs unconditionally and gates on LIVE token state (a session
  // established without a provider remount is still covered); the authClient
  // 401 interceptor is the braces. refreshOnce() is single-flight — with
  // rotated (single-use) refresh tokens, racing refreshes revoke each other.
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!tokenStorage.getAccessToken()) return;
      if (tokenStorage.isExpiringSoon(300)) {
        const ok = await refreshOnce();
        if (ok) {
          const user = restoreAuth();
          setState((prev) => ({ ...prev, user, isAuthenticated: !!user }));
        } else {
          emitSessionExpired();
          setState({ user: null, isAuthenticated: false, loading: false, error: null });
        }
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { user } = await loginAction(credentials);
      resetSessionExpired();
      setState({ user, isAuthenticated: true, loading: false, error: null });
    } catch (err: any) {
      const status = err?.response?.status;
      const message = status === 401 ? 'Invalid username or password'
        : status === 403 ? 'Account is locked or disabled'
        : status === 429 ? 'Too many attempts. Please try again later.'
        : status === 503 ? 'Service temporarily unavailable'
        : 'Authentication failed';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    await logoutAction();
    setState({ user: null, isAuthenticated: false, loading: false, error: null });
  }, []);

  const hasPermission = useCallback(
    (permission: string) => state.user?.permissions.includes(permission) ?? false,
    [state.user],
  );

  const hasRole = useCallback(
    (role: string) => state.user?.role === role,
    [state.user],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) =>
      permissions.some((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) =>
      permissions.every((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Access auth state and actions. Must be used within <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthProvider;
